import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import { CommercialRoutesReducerState } from 'Containers/CommercialRoutes/reducer';
import { Recommendation as RecommendationType } from 'Types/entities';
import { ClientConfig } from 'Utils/apiTypes';
import { DatadogFeatureFlags } from 'Utils/featureFlags';

import { GlobalAppReducerState } from './reducer';
import { EnabledProducts, JVPState } from './types';

const selectAppDomainAsJS = (state: GlobalReducerState): GlobalAppReducerState => state.globalApp;

const selectIsFirstPageView = (path: string) =>
  createSelector(makeSelectGlobalField('pageProps'), (substate) => {
    const pageProps = substate;
    if (pageProps[path] && pageProps[path].viewed) {
      return false;
    }
    return true;
  });

const makeSelectGlobalField = <K extends keyof GlobalAppReducerState>(field: K) =>
  createSelector(
    selectAppDomainAsJS,
    (substate: GlobalAppReducerState): GlobalAppReducerState[K] => substate[field],
  );

const selectCommercialDomainAsJS = (state: GlobalReducerState): CommercialRoutesReducerState =>
  state.commercialApp;

const makeSelectCommercialField = <K extends keyof CommercialRoutesReducerState>(field: K) =>
  createSelector(
    selectCommercialDomainAsJS,
    (substate: CommercialRoutesReducerState): CommercialRoutesReducerState[K] => substate[field],
  );

const makeHasSelectedRecommendation = () =>
  createSelector(
    [makeSelectCommercialField('selectedHealthPlan')],
    (selectedPlan: RecommendationType | Record<string, never>) => {
      if (!isEmpty(selectedPlan)) {
        return true;
      }
      return false;
    },
  );

const makeSelectConfigField = <K extends keyof ClientConfig>(flag: K) =>
  createSelector(makeSelectGlobalField('config'), (cfg) => cfg[flag]);

const makeSelectJvpField = <K extends keyof JVPState>(flag: K) =>
  createSelector(makeSelectGlobalField('jvp'), (cfg) => cfg[flag]);

const makeGetJvpHasPublicationWithFlag = (flag: string) =>
  createSelector(
    [makeSelectJvpField('active'), makeSelectJvpField('upcoming')],
    (active, upcoming) => active?.publication[flag] || upcoming?.publication[flag] || false,
  );

const makeGetJvpPartnerUserIdFromIdentifications = () =>
  createSelector([makeSelectJvpField('employee')], (employee) => {
    if (!employee?.identifications) return null;
    const identity = employee.identifications.find(({ id_scope }) => id_scope === 'partner_user_id');
    return identity?.id_value ?? null;
  });

const makeGetIsCustomerUniversityOfCalifornia = () =>
  createSelector([makeSelectConfigField('builder_customer_key')], (builderCustomerKey) =>
    ['zzzuniversityofcalifornia', 'uoc'].includes(builderCustomerKey ?? ''),
  );

const makeGetShouldShowPreferredProviderTiers = makeGetIsCustomerUniversityOfCalifornia;
const makeGetShouldShowAcceptingNewPatients = makeGetIsCustomerUniversityOfCalifornia;
const makeGetShouldShowGroupName = makeGetIsCustomerUniversityOfCalifornia;

const makeGetSSOConnectionName = () =>
  createSelector(
    [makeSelectJvpField('active'), makeSelectJvpField('upcoming')],
    (active, upcoming): string => {
      const activePub = active?.publication;
      const upcomingPub = upcoming?.publication;

      if (activePub?.sso_enabled) {
        return activePub?.sso_connection_name || '';
      }

      if (upcomingPub?.sso_enabled) {
        return upcomingPub?.sso_connection_name || '';
      }

      return '';
    },
  );

const makeGetEnabledProducts = () =>
  createSelector(
    [
      makeSelectConfigField('has_product_bc'),
      makeSelectConfigField('has_product_go'),
      makeSelectConfigField('has_product_medicare'),
    ],
    (hasProductBc, hasProductGo, hasProductMedicare): EnabledProducts => ({
      bc: hasProductBc,
      go: hasProductGo,
      medicare: hasProductMedicare,
    }),
  );

const makeGetCanChangePayPeriod = () =>
  createSelector(
    [makeSelectConfigField('can_change_pay_period'), makeSelectConfigField('builder_customer_key')],
    (canChangePayPeriod: boolean, builderCustomerKey: string | null): boolean => {
      return canChangePayPeriod && !builderCustomerKey;
    },
  );

const makeGetFeatureFlag = (flag: string) =>
  createSelector(
    [makeSelectConfigField('feature_flags')],
    (featureFlags: DatadogFeatureFlags): boolean | null => {
      return flag in featureFlags ? featureFlags[flag] : null;
    },
  );

const makeSelectAnnualSettings = () => makeSelectGlobalField('annualSettings');

export {
  selectAppDomainAsJS,
  selectCommercialDomainAsJS,
  selectIsFirstPageView,
  makeSelectGlobalField,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeGetEnabledProducts,
  makeSelectJvpField,
  makeGetIsCustomerUniversityOfCalifornia,
  makeGetShouldShowPreferredProviderTiers,
  makeGetShouldShowAcceptingNewPatients,
  makeGetJvpHasPublicationWithFlag,
  makeGetSSOConnectionName,
  makeGetCanChangePayPeriod,
  makeHasSelectedRecommendation,
  makeGetFeatureFlag,
  makeGetJvpPartnerUserIdFromIdentifications,
  makeGetShouldShowGroupName,
  makeSelectAnnualSettings,
};
